import React from 'react'
import { HashRouter, Redirect, Route, Switch } from 'react-router-dom'

import { Navigation } from 'legacy/components/navigation/Navigation'
import { NavigationProvider } from 'legacy/contexts/NavigationContext'

import { AuthDialogs } from 'auth/ui/AuthDialogs'
import { useCarrierContext } from 'auth/ui/context/CarrierContext'
import { useConfigContext } from 'config/ui/ConfigContext'
import { isOnboardingRequired } from 'onboarding/domain/Onboarding'
import { OnboardingDialog } from 'onboarding/ui/OnboardingDialog'
import { PORTAL_LINK_BASE_PATH } from 'rate-command-center/domain/PortalLinkRoute'
import { PORTAL_BASE_PATH } from 'shipperguide/core/domain/PortalRoute'
import ShipperGuide from 'shipperguide/ui/ShipperGuide'
import { useUserAccountContext } from 'user/ui/UserAccountContext'

import { AnnouncementBanner } from '../../announcements/AnnouncementBanner'
import { componentLoader } from '../../utils/component'
import { PrivateRoute } from '../components/private-route/PrivateRoute'
import { WelcomeBrokerModal } from '../components/welcome-modal/WelcomeBrokerModal'
import { useAuthContext } from '../contexts/authContext'
import { ExternalRedirect } from './ExternalRedirect'
import { Fallback } from './Fallback'
import { LEGACY_PORTAL_EXACT_ROUTES, LEGACY_PORTAL_PREFIX_ROUTES } from './legacyPortalPaths'
import { LegacyPortalRedirect } from './LegacyPortalRedirect'
import { PageNotFound } from './PageNotFound'

const AccountVerifyPage = React.lazy(
  componentLoader(() => import('legacy/containers/AccountVerifyPage/AccountVerifyPage'))
)
const AuthPage = React.lazy(componentLoader(() => import('legacy/containers/AuthPage/AuthPage')))
const LoginPage = React.lazy(componentLoader(() => import('auth/ui/LoginPage')))
const LoadsPage = React.lazy(componentLoader(() => import('legacy/containers/LoadsPage/LoadsPage')))
const ResetPasswordPage = React.lazy(componentLoader(() => import('auth/ui/ResetPasswordPage')))
const ResetPasswordCompletedPage = React.lazy(
  componentLoader(() => import('auth/ui/ResetPasswordCompletedPage'))
)
const SetNewPasswordPage = React.lazy(componentLoader(() => import('auth/ui/SetNewPasswordPage')))
const SignupPage = React.lazy(componentLoader(() => import('auth/ui/SignUpPage')))
const AccountCarrierPage = React.lazy(
  componentLoader(() => import('legacy/containers/AccountCarrierPage/AccountCarrierPage'))
)
const ContractsPageApp = React.lazy(
  componentLoader(() => import('legacy/containers/ContractsApp/ContractsApp'))
)
const DriverPage = React.lazy(
  componentLoader(() => import('legacy/containers/DriverPage/DriverPage'))
)
const FleetPage = React.lazy(componentLoader(() => import('legacy/containers/FleetPage/FleetPage')))
const PreferredLanesRouter = React.lazy(
  componentLoader(() => import('preferred-lanes/ui/PreferredLanesRouter'))
)
const RateConfirmation = React.lazy(
  componentLoader(() => import('legacy/containers/RateConPage/RateConfirmation'))
)

const ShipmentsPageApp = React.lazy(
  componentLoader(() => import('legacy/containers/ShipmentsPage/ShipmentsPageApp'))
)
const TruckPage = React.lazy(componentLoader(() => import('legacy/containers/TruckPage/TruckPage')))
const PaymentPage = React.lazy(componentLoader(() => import('payments/ui/PaymentPage')))

const FacilityPage = React.lazy(() => import('facilities/ui/FacilityPage'))

const PortalLinkRouter = React.lazy(
  componentLoader(() => import('rate-command-center/ui/PortalLinkRouter'))
)
const TenderPage = React.lazy(() =>
  import('tender/ui/TenderPage').then(m => ({ default: m.TenderPage }))
)
const DispatcherChangesInfoPage = React.lazy(
  componentLoader(() => import('carrier-guide/ui/DispatcherChangesInfoPage'))
)
const DriverChangesInfoPage = React.lazy(
  componentLoader(() => import('carrier-guide/ui/DriverChangesInfoPage'))
)

export function Router() {
  const [userToken] = useAuthContext()
  const { carrier } = useCarrierContext()
  const account = useUserAccountContext()
  const { configStatus, disableBrokerageFeatures } = useConfigContext()
  const isBrokerageEnabled = !disableBrokerageFeatures.toBoolean()
  const isLogged = Boolean(userToken)
  const isCarrier = carrier?.entityType === 'carrier'
  const isBroker = carrier?.entityType === 'broker'
  const showOnboarding = isLogged && isCarrier && isOnboardingRequired(account)

  return (
    <HashRouter>
      <NavigationProvider>
        <Navigation logged={isLogged} />
      </NavigationProvider>
      <AnnouncementBanner />
      <React.Suspense fallback={<Fallback />}>
        <Switch>
          {configStatus.isDone &&
            (isBrokerageEnabled ? (
              <Redirect from="/" to="/loads" exact />
            ) : (
              <Redirect
                from="/"
                to={`${PORTAL_BASE_PATH}/shipments?utm_source=carrierguide&utm_medium=link_menu&utm_campaign=all_in_one`}
                exact
              />
            ))}
          <Route path="/verify" component={AccountVerifyPage} />
          <Route path="/auth" component={AuthPage} />
          {isBrokerageEnabled ? (
            <Route path="/loads" component={LoadsPage} />
          ) : (
            <Route path="/loads" render={() => <ExternalRedirect targetPath="/load-search" />} />
          )}
          <Route path="/profile/carrier" component={AccountCarrierPage} />
          {isBrokerageEnabled ? (
            <PrivateRoute path="/fleet" component={FleetPage} />
          ) : (
            <Route path="/fleet" render={() => <ExternalRedirect targetPath="/drivers" />} />
          )}
          <Route path="/driver" component={DriverPage} />
          <Route path="/truck" component={TruckPage} />
          <Route path="/rate-con/:refNumber" component={RateConfirmation} />
          {isBrokerageEnabled ? (
            <Route path="/preferred-lanes" component={PreferredLanesRouter} />
          ) : (
            <Route
              path="/preferred-lanes"
              render={() => <ExternalRedirect targetPath="/load-search" />}
            />
          )}
          {isBrokerageEnabled ? (
            <Route path="/shipments" component={ShipmentsPageApp} />
          ) : (
            <Route path="/shipments" render={() => <ExternalRedirect targetPath="/loads" />} />
          )}
          {isBrokerageEnabled ? (
            <Route path="/contracts" component={ContractsPageApp} />
          ) : (
            <Route path="/contracts" render={() => <ExternalRedirect targetPath="/loads" />} />
          )}
          {isBrokerageEnabled ? (
            <Route path="/payments" component={PaymentPage} />
          ) : (
            <Route path="/payments" render={() => <ExternalRedirect targetPath="/settings" />} />
          )}
          <Route path="/login" component={LoginPage} />
          <Route path="/signup" component={SignupPage} />
          <Route path="/reset-password" component={ResetPasswordPage} />
          <Route path="/reset-password-completed" component={ResetPasswordCompletedPage} />
          <Route path="/set-new-password" component={SetNewPasswordPage} />
          <Route path="/facilities/:uuid/review/:reviewUuid" component={FacilityPage} />
          <Route path="/facilities/:uuid" component={FacilityPage} />
          <Route path="/tender/:id" component={TenderPage} />
          {LEGACY_PORTAL_PREFIX_ROUTES.map(path => (
            <Route key={path} path={path} component={LegacyPortalRedirect} />
          ))}
          {LEGACY_PORTAL_EXACT_ROUTES.map(path => (
            <Route key={path} exact path={path} component={LegacyPortalRedirect} />
          ))}
          <Redirect exact from={PORTAL_BASE_PATH} to={`${PORTAL_BASE_PATH}/shipments`} />
          <Route path={`${PORTAL_BASE_PATH}/*`}>
            <ShipperGuide />
          </Route>
          <Route path={PORTAL_LINK_BASE_PATH}>
            <PortalLinkRouter />
          </Route>
          <Route path="/brokerage-dispatcher-changes" component={DispatcherChangesInfoPage} />
          <Route path="/brokerage-driver-changes" component={DriverChangesInfoPage} />
          <Route path="*" component={PageNotFound} />
        </Switch>
      </React.Suspense>
      <AuthDialogs />
      {isBroker && <WelcomeBrokerModal />}
      {showOnboarding && (
        <OnboardingDialog
          show={showOnboarding}
          firstName={account?.firstName}
          lastName={account?.lastName}
          onboardingSteps={account?.onboardingSteps}
        />
      )}
    </HashRouter>
  )
}
